import { Component, EventEmitter, OnInit, Output, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { authBackendEndPointToken } from '../app.config';
import { RoleDisplayNamePipe } from '../shared/role-display-name.pipe';
import { AVAILABLE_PRACTITIONER_ROLE_OPTIONS, SystemRoles } from '../shared/system-roles';
import { MatInputModule } from '@angular/material/input';
interface PendingRequest {
  userId: string;
  fullName: string;
  email?: string;
  phoneNumber?: string;
  specialty?: string;
  requestedAt: string;
  selectedRoles: string[];   // UI state
  busy?: boolean;
}

// Must match AssignableRoles on the backend (matching is case-insensitive there)
const ASSIGNABLE_ROLE_OPTIONS = AVAILABLE_PRACTITIONER_ROLE_OPTIONS
  .filter(r => r.code !== SystemRoles.RemoteDoctor);

@Component({
  selector: 'app-join-requests',
  imports: [DatePipe, FormsModule,MatInputModule, MatButtonModule, MatFormFieldModule, MatIconModule, MatSelectModule, RoleDisplayNamePipe],
  template: `
    @if (message) {
      <div class="msg" [class.err]="isError" role="status">{{ message }}</div>
    }
<h3>Invite a practitioner</h3>
<div class="invite">
  <mat-form-field appearance="outline" class="grow">
    <mat-label>Practitioner email</mat-label>
    <input matInput type="email" [(ngModel)]="inviteEmail" />
  </mat-form-field>
  <mat-form-field appearance="outline" class="grow">
    <mat-label>Roles</mat-label>
    <mat-select multiple [(ngModel)]="inviteRoles">
      @for (role of roleOptions; track role.code) {
        <mat-option [value]="role.code">{{ role.display }}</mat-option>
      }
    </mat-select>
  </mat-form-field>
  <button mat-raised-button color="primary"
          [disabled]="inviting || !inviteEmail.trim() || !inviteRoles.length" (click)="sendInvite()">
    Send invite
  </button>
</div>

@if (invites.length) {
  <h3>Sent invitations</h3>
  @for (i of invites; track i.userId) {
    <div class="card">
      <div class="info">
        <div class="name">{{ i.fullName }}</div>
        <div class="sub">{{ i.email }} · Invited {{ i.invitedAt | date: 'mediumDate' }}</div>
      </div>
      <button mat-stroked-button color="warn" (click)="cancelInvite(i.userId)">Cancel</button>
    </div>
  }
}

<h3>Join requests</h3>
    @for (r of requests; track r.userId) {
      <div class="card">
        <div class="info">
          <div class="name">{{ r.fullName }}</div>
          <div class="sub">{{ r.email }} @if (r.phoneNumber) { · {{ r.phoneNumber }} }</div>
          <div class="sub">
            @if (r.specialty) { {{ r.specialty }} · }
            Requested {{ r.requestedAt | date: 'mediumDate' }}
          </div>
        </div>

        <mat-form-field appearance="outline" class="roles">
          <mat-label>Roles to grant</mat-label>
          <mat-select multiple [(ngModel)]="r.selectedRoles">
  @for (role of roleOptions; track role.code) {
    <mat-option [value]="role.code">{{ role.display }}</mat-option>
  }
</mat-select>
        </mat-form-field>

        <div class="actions">
          <button mat-raised-button color="primary"
                  [disabled]="r.busy || !r.selectedRoles.length" (click)="approve(r)">
            Approve
          </button>
          <button mat-stroked-button color="warn" [disabled]="r.busy" (click)="reject(r)">
            Reject
          </button>
        </div>
      </div>
    } @empty {
      <p class="empty">{{ loading ? 'Loading…' : 'No pending join requests.' }}</p>
    }
  `,
  styles: [`
    :host { display: block; padding: 16px 0; }
    .card { display: flex; flex-wrap: wrap; align-items: center; gap: 16px; padding: 12px 16px;
            margin-bottom: 10px; border: 1px solid var(--mat-sys-outline-variant); border-radius: 12px; }
    .info { flex: 1 1 220px; min-width: 0; }
    .name { font-weight: 600; }
    .sub { font-size: 12px; color: var(--mat-sys-on-surface-variant); }
    .roles { flex: 1 1 240px; }
    .actions { display: flex; gap: 8px; }
    .invite { display: flex; flex-wrap: wrap; gap: 12px; align-items: baseline; }
.grow { flex: 1 1 220px; }
h3 { margin: 16px 0 8px; }
    .empty { text-align: center; padding: 24px; color: var(--mat-sys-on-surface-variant); }
    .msg { padding: 8px 12px; border-radius: 8px; margin-bottom: 8px; background: var(--mat-sys-secondary-container); }
    .msg.err { background: var(--mat-sys-error-container); color: var(--mat-sys-on-error-container); }
  `]
})
export class JoinRequestsComponent implements OnInit {
  private http = inject(HttpClient);
  private base = inject(authBackendEndPointToken);

  @Output() countChange = new EventEmitter<number>();
@Output() approved = new EventEmitter<void>();
roleOptions = ASSIGNABLE_ROLE_OPTIONS;
  requests: PendingRequest[] = [];
  loading = true;
  message: string | null = null;
  isError = false;
invites: { userId: string; fullName: string; email?: string; invitedAt: string }[] = [];
inviteEmail = '';
inviteRoles: string[] = [SystemRoles.Doctor];
inviting = false;

private loadInvites(): void {
  this.http.get<typeof this.invites>(`${this.base}/api/membership-requests/invitations`)
    .subscribe({ next: list => (this.invites = list ?? []) });
}

sendInvite(): void {
  this.inviting = true;
  this.http.post<{ message: string }>(`${this.base}/api/membership-requests/invitations`,
    { email: this.inviteEmail.trim(), roles: this.inviteRoles })
    .subscribe({
      next: res => { this.show(res.message, false); this.inviteEmail = ''; this.inviting = false; this.loadInvites(); },
      error: e => { this.inviting = false; this.show(e?.error?.message ?? 'Could not send invitation.', true); }
    });
}

cancelInvite(userId: string): void {
  this.http.delete<{ message: string }>(`${this.base}/api/membership-requests/invitations/${userId}`)
    .subscribe({
      next: res => { this.show(res.message, false); this.loadInvites(); },
      error: e => this.show(e?.error?.message ?? 'Could not cancel.', true)
    });
}
  ngOnInit(): void {
    this.load();
    this.loadInvites();
  }

  private load(): void {
    this.loading = true;
    
    this.http.get<Omit<PendingRequest, 'selectedRoles'>[]>(`${this.base}/api/membership-requests/pending`)
      .subscribe({
        next: list => {
          this.requests = (list ?? []).map(r => ({ ...r, selectedRoles: ['Doctor'] }));
          this.requests = (list ?? []).map(r => ({ ...r, selectedRoles: [SystemRoles.Doctor] }));
          this.countChange.emit(this.requests.length);
          this.loading = false;
        },
        error: () => { this.loading = false; this.show('Could not load join requests.', true); }
      });
  }

  approve(r: PendingRequest): void {
  r.busy = true;
  this.http.post<{ message: string }>(
    `${this.base}/api/membership-requests/${r.userId}/approve`, { roles: r.selectedRoles })
    .subscribe({
      next: res => { this.show(res.message, false); this.remove(r); this.approved.emit(); },   // changed
      error: e => { r.busy = false; this.show(e?.error?.message ?? 'Could not approve.', true); }
    });
}
  reject(r: PendingRequest): void {
    if (!confirm(`Reject ${r.fullName}'s request to join?`)) return;
    r.busy = true;
    this.http.post<{ message: string }>(`${this.base}/api/membership-requests/${r.userId}/reject`, {})
      .subscribe({
        next: res => { this.show(res.message, false); this.remove(r); },
        error: e => { r.busy = false; this.show(e?.error?.message ?? 'Could not reject.', true); }
      });
  }

  private remove(r: PendingRequest): void {
    this.requests = this.requests.filter(x => x.userId !== r.userId);
    this.countChange.emit(this.requests.length);
  }

  private show(text: string, error: boolean): void {
    this.message = text;
    this.isError = error;
    setTimeout(() => (this.message = null), 6000);
  }
}